/**
 * AlterMe Conjoint Simulator — Calculation Engine
 * ─────────────────────────────────────────────────
 * Pure functions. Zero side effects. Every formula matches the Excel simulator.
 *
 * IMPORTANT: These functions operate on the raw conjoint data from conjointData.ts.
 * Never hardcode numeric constants here — always reference the data layer or
 * accept values via the Assumptions parameter.
 */

import type {
  Assumptions,
  PackageConfig,
  PnLResult,
  SegmentDemand,
  SensitivityCell,
  BundleResult,
  BreakevenResult,
  RevenueScenario,
  FeatureWTP,
  SegmentWTPCeiling,
  OptimalConfig,
  ContributionResult,
  PortfolioPnLResult,
} from '../types';

import {
  PRICE_POINTS,
  PRICE_TO_RATE,
  FEATURE_LIFT,
  FEATURE_NAMES,
  SEGMENTS,
  DEFAULT_ASSUMPTIONS,
} from '../data/conjointData';


// ─── CORE LOOKUPS ───

/**
 * Get the base concept-level choice rate for a price point.
 * Returns 0 if price is not in the tested set.
 */
export function getBaseChoiceRate(price: number): number {
  return PRICE_TO_RATE[price] ?? 0;
}

/**
 * Get interpolated choice rate for any price (linear interpolation between tested points).
 * Useful for smooth demand curve visualization.
 */
export function getInterpolatedChoiceRate(price: number): number {
  const sorted = [...PRICE_POINTS].sort((a, b) => a.price - b.price);

  if (price <= sorted[0].price) return sorted[0].choiceRate;
  if (price >= sorted[sorted.length - 1].price) return sorted[sorted.length - 1].choiceRate;

  for (let i = 0; i < sorted.length - 1; i++) {
    if (price >= sorted[i].price && price <= sorted[i + 1].price) {
      const t = (price - sorted[i].price) / (sorted[i + 1].price - sorted[i].price);
      return sorted[i].choiceRate + t * (sorted[i + 1].choiceRate - sorted[i].choiceRate);
    }
  }

  return 0;
}


// ─── FEATURE CALCULATIONS ───

/**
 * Calculate total feature lift from a list of enabled feature names.
 * Lift is the sum of individual feature lifts (included% - excluded%).
 */
export function calculateTotalFeatureLift(enabledFeatures: string[]): number {
  return enabledFeatures.reduce((sum, name) => {
    return sum + (FEATURE_LIFT[name] ?? 0);
  }, 0);
}

/**
 * Calculate adjusted choice rate incorporating feature lifts.
 * Formula: baseRate * (1 + totalLift * featureScaling)
 */
export function calculateAdjustedChoiceRate(
  price: number,
  enabledFeatures: string[],
  featureScaling: number = DEFAULT_ASSUMPTIONS.featureScaling
): number {
  const baseRate = getBaseChoiceRate(price);
  const totalLift = calculateTotalFeatureLift(enabledFeatures);
  return baseRate * (1 + totalLift * featureScaling);
}


// ─── FUNNEL MODEL ───

/**
 * Calculate S2 units from S1 leads and adjusted choice rate.
 * The adjusted choice rate IS the end-to-end concept-level probability;
 * S2 units = S1 leads * adjusted choice rate.
 */
export function calculateS2Units(s1Leads: number, adjustedChoiceRate: number): number {
  return s1Leads * adjustedChoiceRate;
}

/**
 * Calculate the implied in-consultation close rate.
 * Decomposes the adjusted choice rate through the kit-return and consultation funnel.
 * impliedCloseRate = adjustedChoiceRate / (kitReturnRate * consultRate)
 */
export function calculateImpliedCloseRate(
  adjustedChoiceRate: number,
  kitReturnRate: number,
  consultRate: number
): number {
  const denominator = kitReturnRate * consultRate;
  if (denominator === 0) return 0;
  return adjustedChoiceRate / denominator;
}


// ─── COST HELPERS (CFO FEB 2026 Formula) ───

/**
 * Feature COGS for a specific bundle.
 */
export function getFeatureCOGS(enabledFeatures: string[], assumptions: Assumptions): number {
  return enabledFeatures.reduce(
    (sum, f) => sum + (assumptions.featureCosts?.[f] || 0), 0
  );
}

/**
 * Feature COGS when ALL features are enabled.
 */
export function getFullFeatureCOGS(assumptions: Assumptions): number {
  return Object.values(assumptions.featureCosts || {}).reduce((s, v) => s + v, 0);
}

/**
 * S1 acquisition cost per S2 sale = S1 cost per kit / S2 close rate.
 * At 20% close rate: $93.39 / 0.20 = $466.95 ("almost 5 S1s per sale").
 */
export function getS1AcquisitionCost(assumptions: Assumptions): number {
  if (assumptions.s2CloseRate <= 0) return 0;
  return assumptions.s1CostPerUnit / assumptions.s2CloseRate;
}

/**
 * Total all-in cost per S2 unit for a specific feature bundle.
 * Includes: S1 acquisition + S2 rep + feature COGS.
 * Revenue deductions (fees/refunds) are applied on the revenue side, not here.
 */
export function getAllInCostPerUnit(enabledFeatures: string[], assumptions: Assumptions): number {
  return getS1AcquisitionCost(assumptions) + assumptions.s2RepCost + getFeatureCOGS(enabledFeatures, assumptions);
}

/**
 * Net income per S2 unit = net revenue per unit - all-in costs per unit.
 * This matches the CFO's formula: Price × (1 - deductionRate) - S1acq - S2rep - featureCOGS.
 */
export function getNetIncomePerUnit(price: number, enabledFeatures: string[], assumptions: Assumptions): number {
  const netRevenuePerUnit = price * (1 - assumptions.totalDeductionRate);
  return netRevenuePerUnit - getAllInCostPerUnit(enabledFeatures, assumptions);
}

// ─── P&L CALCULATION (CFO FEB 2026 Formula) ───

/**
 * Full P&L calculation for a package configuration.
 * Uses CFO's formula: Net Revenue - Fixed Costs - S1 Acquisition - S2 Rep - Feature COGS.
 */
export function calculatePnL(config: PackageConfig, assumptions: Assumptions): PnLResult {
  const baseChoiceRate = getBaseChoiceRate(config.price);
  const totalFeatureLift = calculateTotalFeatureLift(config.enabledFeatures);
  const adjustedChoiceRate = baseChoiceRate * (1 + totalFeatureLift * assumptions.featureScaling);

  const s2Units = calculateS2Units(assumptions.s1Leads, adjustedChoiceRate);
  const grossRevenue = s2Units * config.price;
  const netRevenue = grossRevenue * (1 - assumptions.totalDeductionRate);
  const fixedCosts = assumptions.fixedCosts;

  const s1AcquisitionCosts = s2Units * getS1AcquisitionCost(assumptions);
  const s2RepCosts = s2Units * assumptions.s2RepCost;
  const featureCOGS = s2Units * getFeatureCOGS(config.enabledFeatures, assumptions);

  const monthlyPnL = netRevenue - fixedCosts - s1AcquisitionCosts - s2RepCosts - featureCOGS;

  const netIncomePerUnit = getNetIncomePerUnit(config.price, config.enabledFeatures, assumptions);
  const breakevenUnits = netIncomePerUnit > 0 ? fixedCosts / netIncomePerUnit : Infinity;
  const gapToBreakeven = s2Units - breakevenUnits;

  const impliedCloseRate = calculateImpliedCloseRate(
    adjustedChoiceRate,
    assumptions.kitReturnRate,
    assumptions.consultRate
  );

  const revenuePerShowing = config.price * adjustedChoiceRate;
  const revenuePer1000 = revenuePerShowing * 1000;

  return {
    baseChoiceRate,
    totalFeatureLift,
    adjustedChoiceRate,
    s2Units,
    grossRevenue,
    netRevenue,
    fixedCosts,
    s1AcquisitionCosts,
    s2RepCosts,
    featureCOGS,
    monthlyPnL,
    netIncomePerUnit,
    breakevenUnits: breakevenUnits === Infinity ? 999999 : Math.ceil(breakevenUnits),
    gapToBreakeven,
    impliedCloseRate,
    revenuePerShowing,
    revenuePer1000,
  };
}


// ─── BREAKEVEN ANALYSIS ───

/**
 * Calculate breakeven metrics for each price point.
 */
export function calculateBreakevenAnalysis(assumptions: Assumptions): BreakevenResult[] {
  const allFeatures = Object.keys(assumptions.featureCosts || {});
  return PRICE_POINTS.map(pt => {
    const netRevenuePerUnit = pt.price * (1 - assumptions.totalDeductionRate);
    const marginPerUnit = netRevenuePerUnit - getAllInCostPerUnit(allFeatures, assumptions);
    const breakevenUnits = marginPerUnit > 0 ? assumptions.fixedCosts / marginPerUnit : 999999;
    const s1LeadsNeeded = pt.choiceRate > 0 ? breakevenUnits / pt.choiceRate : 999999;

    return {
      price: pt.price,
      choiceRate: pt.choiceRate,
      netRevenuePerUnit,
      marginPerUnit,
      breakevenUnits: Math.ceil(breakevenUnits),
      s1LeadsNeeded: Math.ceil(s1LeadsNeeded),
      achievable: s1LeadsNeeded <= 8501,
    };
  });
}


// ─── SENSITIVITY ANALYSIS ───

/**
 * Generate the full sensitivity grid: Price × S1 Leads.
 * Returns S2 units, net revenue, and P&L for each cell.
 */
export function generateSensitivityGrid(
  s1Range: number[],
  assumptions: Assumptions
): SensitivityCell[][] {
  const allFeatures = Object.keys(assumptions.featureCosts || {});
  const costPerUnit = getAllInCostPerUnit(allFeatures, assumptions);
  return PRICE_POINTS.map(pt => {
    return s1Range.map(s1 => {
      const s2Units = s1 * pt.choiceRate;
      const netRevenue = s2Units * pt.price * (1 - assumptions.totalDeductionRate);
      const pnl = netRevenue - assumptions.fixedCosts - s2Units * costPerUnit;

      return {
        s1Leads: s1,
        price: pt.price,
        s2Units: Math.round(s2Units),
        netRevenue: Math.round(netRevenue),
        pnl: Math.round(pnl),
      };
    });
  });
}


/**
 * Portfolio sensitivity: varies S1 leads across the current package portfolio.
 * Returns one row per S1 level with the combined portfolio P&L.
 */
export function generatePortfolioSensitivityGrid(
  s1Range: number[],
  packages: PackageConfig[],
  assumptions: Assumptions
): { s1Leads: number; totalUnits: number; totalRevenue: number; pnl: number }[] {
  return s1Range.map(s1 => {
    const adjusted = { ...assumptions, s1Leads: s1 };
    const result = calculatePortfolioPnL(packages, adjusted);
    return {
      s1Leads: s1,
      totalUnits: Math.round(result.totalS2UnitsSplit),
      totalRevenue: Math.round(result.totalNetRevenueSplit),
      pnl: Math.round(result.portfolioPnLSplit),
    };
  });
}


// ─── SEGMENT DEMAND ───

/**
 * Calculate weighted segment demand at a given price.
 */
export function calculateWeightedSegmentDemand(price: number): SegmentDemand[] {
  return SEGMENTS.map(seg => {
    const choiceRate = seg.priceCurve[price] ?? 0;
    return {
      segmentName: seg.name,
      weight: seg.weight,
      choiceRate,
      weightedDemand: seg.weight * choiceRate,
    };
  });
}

/**
 * Total weighted demand across all segments at a price.
 * Should approximately equal the overall concept-level choice rate.
 */
export function calculateTotalWeightedDemand(price: number): number {
  return calculateWeightedSegmentDemand(price).reduce((sum, sd) => sum + sd.weightedDemand, 0);
}

/**
 * Per-segment adjusted demand for a configured package.
 * Applies overall feature lifts uniformly to each segment's base rate at the package price.
 * Feature lifts are population-level; segment-specific feature interactions were not measured.
 */
export function calculateSegmentPackageDemand(
  price: number,
  enabledFeatures: string[],
  featureScaling: number
): { segmentName: string; segmentRate: number; weight: number; color: string }[] {
  const totalLift = calculateTotalFeatureLift(enabledFeatures);
  return SEGMENTS.map(seg => {
    const baseRate = seg.priceCurve[price] ?? 0;
    const adjustedRate = baseRate * (1 + totalLift * featureScaling);
    return {
      segmentName: seg.name,
      segmentRate: adjustedRate,
      weight: seg.weight,
      color: seg.color,
    };
  });
}

/**
 * Combined market capture across multiple packages (ceiling estimate).
 * For each segment, takes the MAX adjusted rate across all packages.
 * Assumes zero cannibalization between tiers.
 */
export function calculateCombinedMarketCapture(
  packages: { price: number; enabledFeatures: string[] }[],
  featureScaling: number
): { combined: number; perSegment: { name: string; maxRate: number; weight: number }[] } {
  const segmentMaxRates = SEGMENTS.map(seg => {
    const rates = packages.map(pkg => {
      const baseRate = seg.priceCurve[pkg.price] ?? 0;
      const totalLift = calculateTotalFeatureLift(pkg.enabledFeatures);
      return baseRate * (1 + totalLift * featureScaling);
    });
    return {
      name: seg.name,
      maxRate: Math.max(...rates),
      weight: seg.weight,
    };
  });
  const combined = segmentMaxRates.reduce((sum, s) => sum + s.weight * s.maxRate, 0);
  return { combined, perSegment: segmentMaxRates };
}


// ─── FEATURE BUNDLE OPTIMIZER ───

/**
 * Enumerate all 2^7 = 128 feature bundle combinations.
 * Returns them ranked by P&L at the given price and assumptions.
 */
export function rankAllFeatureBundles(
  price: number,
  assumptions: Assumptions
): BundleResult[] {
  const results: BundleResult[] = [];
  const n = FEATURE_NAMES.length; // 7
  const totalCombos = Math.pow(2, n); // 128

  for (let mask = 0; mask < totalCombos; mask++) {
    const features: string[] = [];
    for (let i = 0; i < n; i++) {
      if (mask & (1 << i)) {
        features.push(FEATURE_NAMES[i]);
      }
    }

    const totalLift = calculateTotalFeatureLift(features);
    const baseRate = getBaseChoiceRate(price);
    const adjustedChoiceRate = baseRate * (1 + totalLift * assumptions.featureScaling);
    const s2Units = assumptions.s1Leads * adjustedChoiceRate;
    const netRevenue = s2Units * price * (1 - assumptions.totalDeductionRate);
    const costPerUnit = getAllInCostPerUnit(features, assumptions);
    const pnl = netRevenue - assumptions.fixedCosts - s2Units * costPerUnit;

    results.push({
      features,
      featureCount: features.length,
      totalLift,
      adjustedChoiceRate,
      s2Units: Math.round(s2Units),
      pnl: Math.round(pnl),
    });
  }

  // Sort by P&L descending
  results.sort((a, b) => b.pnl - a.pnl);

  return results;
}


// ─── REVENUE SCENARIOS ───

/**
 * Generate revenue comparison scenarios for the Financial Model tab.
 */
export function generateRevenueScenarios(assumptions: Assumptions): RevenueScenario[] {
  // Current model (their internal targets)
  const allFeatures = Object.keys(assumptions.featureCosts || {});
  const costPerUnit = getAllInCostPerUnit(allFeatures, assumptions);
  const currentModel: RevenueScenario = {
    name: 'Current Model (FEB GOALS)',
    price: 1899,
    choiceRate: 0.202,
    s2Units: 1072,
    netRevenue: 1072 * 1899 * (1 - assumptions.totalDeductionRate),
    pnl: 1072 * 1899 * (1 - assumptions.totalDeductionRate) - assumptions.fixedCosts - 1072 * costPerUnit,
    isCurrentModel: true,
  };

  const conjointScenarios: RevenueScenario[] = PRICE_POINTS.slice().reverse().map(pt => {
    const s2Units = assumptions.s1Leads * pt.choiceRate;
    const netRevenue = s2Units * pt.price * (1 - assumptions.totalDeductionRate);
    const pnl = netRevenue - assumptions.fixedCosts - s2Units * costPerUnit;

    return {
      name: `Conjoint @ $${pt.price.toLocaleString()}`,
      price: pt.price,
      choiceRate: pt.choiceRate,
      s2Units: Math.round(s2Units),
      netRevenue: Math.round(netRevenue),
      pnl: Math.round(pnl),
    };
  });

  return [currentModel, ...conjointScenarios];
}


// ─── INTERPOLATED DEMAND CURVE ───

/**
 * Generate a smooth demand curve for charting (linear interpolation between tested points).
 * Returns `points` evenly-spaced {price, rate} pairs.
 */
export function interpolateDemandCurve(points: number = 50): { price: number; rate: number }[] {
  const sorted = [...PRICE_POINTS].sort((a, b) => a.price - b.price);
  const minPrice = sorted[0].price;
  const maxPrice = sorted[sorted.length - 1].price;
  const step = (maxPrice - minPrice) / (points - 1);

  const curve: { price: number; rate: number }[] = [];
  for (let i = 0; i < points; i++) {
    const price = minPrice + step * i;
    curve.push({ price: Math.round(price), rate: getInterpolatedChoiceRate(price) });
  }

  return curve;
}


// ─── MARGINAL FEATURE VALUE ───

/**
 * For each feature NOT in the current bundle, calculate the P&L delta of adding it.
 * For each feature IN the current bundle, calculate the P&L delta of removing it.
 */
export function calculateMarginalFeatureValues(
  currentFeatures: string[],
  price: number,
  assumptions: Assumptions
): { name: string; isEnabled: boolean; marginalPnL: number }[] {
  const currentConfig: PackageConfig = {
    name: 'current',
    price,
    paymentTerm: 'monthlyZeroApr',
    enabledFeatures: currentFeatures,
  };
  const currentPnL = calculatePnL(currentConfig, assumptions).monthlyPnL;

  return FEATURE_NAMES.map(name => {
    const isEnabled = currentFeatures.includes(name);
    let altFeatures: string[];

    if (isEnabled) {
      // What happens if we remove it
      altFeatures = currentFeatures.filter(f => f !== name);
    } else {
      // What happens if we add it
      altFeatures = [...currentFeatures, name];
    }

    const altConfig: PackageConfig = {
      name: 'alt',
      price,
      paymentTerm: 'monthlyZeroApr',
      enabledFeatures: altFeatures,
    };
    const altPnL = calculatePnL(altConfig, assumptions).monthlyPnL;

    return {
      name,
      isEnabled,
      marginalPnL: isEnabled ? (currentPnL - altPnL) : (altPnL - currentPnL),
      // If enabled: positive = keeping it adds value
      // If disabled: positive = adding it would increase P&L
    };
  }).sort((a, b) => b.marginalPnL - a.marginalPnL);
}


// ─── WILLINGNESS TO PAY ───

/**
 * Interpolate choice rate with linear extrapolation beyond tested price bounds.
 * Used only by WTP calculations where we need demand estimates below the
 * minimum tested price ($499). The standard getInterpolatedChoiceRate clamps,
 * which breaks binary search at boundary prices.
 */
function getExtrapolatedChoiceRate(price: number): number {
  const sorted = [...PRICE_POINTS].sort((a, b) => a.price - b.price);

  if (price < sorted[0].price && sorted.length >= 2) {
    const slope = (sorted[1].choiceRate - sorted[0].choiceRate) / (sorted[1].price - sorted[0].price);
    return Math.min(1, Math.max(0, sorted[0].choiceRate + slope * (price - sorted[0].price)));
  }

  if (price > sorted[sorted.length - 1].price && sorted.length >= 2) {
    const n = sorted.length;
    const slope = (sorted[n - 1].choiceRate - sorted[n - 2].choiceRate) / (sorted[n - 1].price - sorted[n - 2].price);
    return Math.max(0, sorted[n - 1].choiceRate + slope * (price - sorted[n - 1].price));
  }

  return getInterpolatedChoiceRate(price);
}

/**
 * Calculate the implied dollar value of a feature using demand-equivalence.
 * Method: At the given basePrice, adding the feature increases demand by X%.
 * Find the price drop (without the feature) needed to achieve the same demand.
 * The difference is the feature's implied WTP in dollars.
 */
export function calculateFeatureWTP(
  featureName: string,
  basePrice: number,
  assumptions: Assumptions
): number {
  const lift = FEATURE_LIFT[featureName] ?? 0;
  const baseRate = getBaseChoiceRate(basePrice);
  if (baseRate === 0) return 0;

  const rateWithFeature = baseRate * (1 + lift * assumptions.featureScaling);

  let lo = 0, hi = basePrice;
  for (let i = 0; i < 50; i++) {
    const mid = (lo + hi) / 2;
    const rateAtMid = getExtrapolatedChoiceRate(mid);
    if (rateAtMid < rateWithFeature) hi = mid;
    else lo = mid;
  }
  const equivalentPrice = (lo + hi) / 2;
  return Math.max(0, basePrice - equivalentPrice);
}

/**
 * Calculate WTP for all features, ranked by dollar value.
 */
export function calculateAllFeatureWTP(
  basePrice: number,
  assumptions: Assumptions
): FeatureWTP[] {
  const results = FEATURE_NAMES.map((name, i) => ({
    name,
    lift: FEATURE_LIFT[name] ?? 0,
    impliedDollarValue: calculateFeatureWTP(name, basePrice, assumptions),
    rank: i + 1,
  }));
  results.sort((a, b) => b.impliedDollarValue - a.impliedDollarValue);
  results.forEach((r, i) => r.rank = i + 1);
  return results;
}

/**
 * Calculate the maximum price a segment will tolerate at a given demand threshold.
 * Uses interpolation within the segment's price curve.
 */
export function calculateSegmentWTPCeiling(
  segment: typeof SEGMENTS[number],
  demandThreshold: number = 0.10
): SegmentWTPCeiling {
  const prices = Object.keys(segment.priceCurve).map(Number).sort((a, b) => a - b);

  let peakDemand = 0;
  let peakDemandPrice = 0;
  for (const p of prices) {
    const r = segment.priceCurve[p];
    if (r > peakDemand) {
      peakDemand = r;
      peakDemandPrice = p;
    }
  }

  // Find where demand drops below threshold via interpolation
  let ceiling = 0;
  let demandAtCeiling = 0;

  for (let i = 0; i < prices.length; i++) {
    const rate = segment.priceCurve[prices[i]];
    if (rate >= demandThreshold) {
      ceiling = prices[i];
      demandAtCeiling = rate;
    }
    if (i < prices.length - 1) {
      const nextRate = segment.priceCurve[prices[i + 1]];
      if (rate >= demandThreshold && nextRate < demandThreshold) {
        // Interpolate the exact crossing point
        const t = (demandThreshold - rate) / (nextRate - rate);
        ceiling = prices[i] + t * (prices[i + 1] - prices[i]);
        demandAtCeiling = demandThreshold;
        break;
      }
    }
  }

  return {
    segmentName: segment.name,
    ceiling: Math.round(ceiling),
    demandAtCeiling,
    color: segment.color,
    peakDemand,
    peakDemandPrice,
  };
}

/**
 * Calculate all segment WTP ceilings.
 */
export function calculateAllSegmentWTPCeilings(
  demandThreshold: number = 0.10
): SegmentWTPCeiling[] {
  return SEGMENTS.map(seg => calculateSegmentWTPCeiling(seg, demandThreshold));
}

/**
 * Calculate a perceived value score for a package configuration.
 * Score = total feature lift * feature scaling (0 to 1 range).
 */
export function calculatePriceValueScore(
  enabledFeatures: string[],
  featureScaling: number
): number {
  const totalLift = calculateTotalFeatureLift(enabledFeatures);
  return totalLift * featureScaling;
}


/**
 * Find the optimal price + feature bundle that maximizes monthly P&L.
 * Searches across all tested prices and all 128 feature combinations.
 */
export function getOptimalConfig(assumptions: Assumptions): OptimalConfig {
  let best: OptimalConfig | null = null;

  for (const pt of PRICE_POINTS) {
    const bundles = rankAllFeatureBundles(pt.price, assumptions);
    const top = bundles[0];
    if (!best || top.pnl > best.monthlyPnL) {
      const marginPerUnit = getNetIncomePerUnit(pt.price, top.features, assumptions);
      best = {
        price: pt.price,
        features: top.features,
        featureCount: top.featureCount,
        adjustedChoiceRate: top.adjustedChoiceRate,
        s2Units: top.s2Units,
        monthlyPnL: top.pnl,
        annualPnL: top.pnl * 12,
        netIncomePerUnit: Math.round(marginPerUnit),
      };
    }
  }

  return best!;
}


// ─── PORTFOLIO P&L (Multi-Package Model) ───

/**
 * Resolve per-package cost parameters by merging optional overrides with global assumptions.
 * Any field set in costOverrides takes precedence; otherwise falls back to global.
 */
export function resolvePackageCosts(pkg: PackageConfig, assumptions: Assumptions) {
  const o = pkg.costOverrides || {};
  return {
    s1CostPerUnit: o.s1CostPerUnit ?? assumptions.s1CostPerUnit,
    s2CloseRate: o.s2CloseRate ?? assumptions.s2CloseRate,
    totalDeductionRate: o.totalDeductionRate ?? assumptions.totalDeductionRate,
    s2RepCost: o.s2RepCost ?? assumptions.s2RepCost,
    kitReturnRate: o.kitReturnRate ?? assumptions.kitReturnRate,
    consultRate: o.consultRate ?? assumptions.consultRate,
  };
}

/**
 * Calculate split-demand allocation across packages using segment-level proportional sharing.
 *
 * For each segment:
 *   1. Compute each package's adjusted choice rate at that segment
 *   2. The segment's total addressable demand = max(individual rates)
 *   3. Allocate proportionally: pkg_share = (pkg_rate / sum_of_rates) * max_rate
 *
 * This ensures total units never exceed the ceiling and higher-demand packages
 * get a larger share of each segment.
 */
function calculateSplitDemandUnits(
  packages: PackageConfig[],
  assumptions: Assumptions
): number[] {
  const splitUnits = packages.map(() => 0);

  for (const seg of SEGMENTS) {
    const rates = packages.map(pkg => {
      const baseRate = seg.priceCurve[pkg.price] ?? 0;
      const totalLift = calculateTotalFeatureLift(pkg.enabledFeatures);
      return baseRate * (1 + totalLift * assumptions.featureScaling);
    });

    const sumRates = rates.reduce((s, r) => s + r, 0);
    if (sumRates === 0) continue;

    const maxRate = Math.max(...rates);

    for (let i = 0; i < packages.length; i++) {
      const share = rates[i] / sumRates;
      const segUnits = assumptions.s1Leads * maxRate * seg.weight;
      splitUnits[i] += segUnits * share;
    }
  }

  return splitUnits;
}

/**
 * Apply cannibalization: a fraction of higher-priced package demand trades down
 * to cheaper packages when multiple tiers exist.
 */
function applyCannibalization(
  splitUnits: number[],
  packages: PackageConfig[],
  cannibalizationRate: number
): number[] {
  if (cannibalizationRate <= 0 || packages.length <= 1) return splitUnits;

  const result = [...splitUnits];
  const original = [...splitUnits]; // Use original values to prevent cascading
  const priceOrder = packages.map((p, i) => ({ price: p.price, idx: i })).sort((a, b) => b.price - a.price);

  for (let rank = 0; rank < priceOrder.length - 1; rank++) {
    const expensive = priceOrder[rank];
    const cheaper = priceOrder[rank + 1];
    const tradeDown = original[expensive.idx] * cannibalizationRate;
    result[expensive.idx] -= tradeDown;
    result[cheaper.idx] += tradeDown;
  }

  return result;
}

/**
 * Portfolio-level P&L: fixed costs applied once, each package contributes margin.
 * Uses per-package cost overrides (S1 cost, S2 close rate, deduction rate, etc.)
 * merged with global assumptions via resolvePackageCosts.
 */
export function calculatePortfolioPnL(
  packages: PackageConfig[],
  assumptions: Assumptions
): PortfolioPnLResult {
  const rawSplitUnits = calculateSplitDemandUnits(packages, assumptions);
  const splitUnitsArr = applyCannibalization(rawSplitUnits, packages, assumptions.cannibalizationRate ?? 0);

  const pkgResults: ContributionResult[] = packages.map((pkg, i) => {
    const costs = resolvePackageCosts(pkg, assumptions);

    const baseChoiceRate = getBaseChoiceRate(pkg.price);
    const totalFeatureLift = calculateTotalFeatureLift(pkg.enabledFeatures);
    const adjustedChoiceRate = baseChoiceRate * (1 + totalFeatureLift * assumptions.featureScaling);
    const impliedCloseRate = calculateImpliedCloseRate(
      adjustedChoiceRate, costs.kitReturnRate, costs.consultRate
    );

    const s2UnitsCeiling = assumptions.s1Leads * adjustedChoiceRate;
    const s2UnitsSplit = splitUnitsArr[i];

    const s1AcqPerUnit = costs.s2CloseRate > 0 ? costs.s1CostPerUnit / costs.s2CloseRate : 0;
    const featureCOGSPerUnit = getFeatureCOGS(pkg.enabledFeatures, assumptions);
    const netRevenuePerUnit = pkg.price * (1 - costs.totalDeductionRate);
    const netIncomePerUnit = netRevenuePerUnit - s1AcqPerUnit - costs.s2RepCost - featureCOGSPerUnit;

    const buildResult = (units: number) => {
      const gross = units * pkg.price;
      const net = gross * (1 - costs.totalDeductionRate);
      const s1Acq = units * s1AcqPerUnit;
      const s2Rep = units * costs.s2RepCost;
      const fCOGS = units * featureCOGSPerUnit;
      const contribution = net - s1Acq - s2Rep - fCOGS;
      return { gross, net, s1Acq, s2Rep, fCOGS, contribution };
    };

    const ceiling = buildResult(s2UnitsCeiling);
    const split = buildResult(s2UnitsSplit);

    return {
      baseChoiceRate,
      totalFeatureLift,
      adjustedChoiceRate,
      impliedCloseRate,
      s2UnitsCeiling,
      s2UnitsSplit,
      grossRevenueCeiling: ceiling.gross,
      grossRevenueSplit: split.gross,
      netRevenueCeiling: ceiling.net,
      netRevenueSplit: split.net,
      s1AcquisitionCostsCeiling: ceiling.s1Acq,
      s2RepCostsCeiling: ceiling.s2Rep,
      featureCOGSCeiling: ceiling.fCOGS,
      s1AcquisitionCostsSplit: split.s1Acq,
      s2RepCostsSplit: split.s2Rep,
      featureCOGSSplit: split.fCOGS,
      contributionMarginCeiling: ceiling.contribution,
      contributionMarginSplit: split.contribution,
      netIncomePerUnit,
      revenuePerShowing: pkg.price * adjustedChoiceRate,
    };
  });

  const totalS2UnitsCeiling = pkgResults.reduce((s, p) => s + p.s2UnitsCeiling, 0);
  const totalS2UnitsSplit = pkgResults.reduce((s, p) => s + p.s2UnitsSplit, 0);
  const totalNetRevenueCeiling = pkgResults.reduce((s, p) => s + p.netRevenueCeiling, 0);
  const totalNetRevenueSplit = pkgResults.reduce((s, p) => s + p.netRevenueSplit, 0);
  const totalContributionCeiling = pkgResults.reduce((s, p) => s + p.contributionMarginCeiling, 0);
  const totalContributionSplit = pkgResults.reduce((s, p) => s + p.contributionMarginSplit, 0);

  const fixedCosts = assumptions.fixedCosts;
  const portfolioPnLCeiling = totalContributionCeiling - fixedCosts;
  const portfolioPnLSplit = totalContributionSplit - fixedCosts;

  const weightedNI = totalS2UnitsSplit > 0
    ? pkgResults.reduce((s, p) => s + p.netIncomePerUnit * p.s2UnitsSplit, 0) / totalS2UnitsSplit
    : 0;
  const portfolioBreakevenUnits = weightedNI > 0 ? Math.ceil(fixedCosts / weightedNI) : 999999;

  const contributionMarginPctSplit = totalNetRevenueSplit > 0
    ? totalContributionSplit / totalNetRevenueSplit
    : 0;

  const weightedAvgSellingPrice = totalS2UnitsSplit > 0
    ? pkgResults.reduce((s, p) => s + p.grossRevenueSplit, 0) / totalS2UnitsSplit
    : 0;

  const totalS1Investment = pkgResults.reduce((s, p) => s + p.s1AcquisitionCostsSplit, 0);
  const s1ROI = totalS1Investment > 0 ? (totalNetRevenueSplit - totalS1Investment) / totalS1Investment : 0;

  // If already profitable, breakeven is now. If monthly P&L is negative, the business
  // never breaks even at the current run rate (fixed costs recur every month).
  const monthsToBreakeven = portfolioPnLSplit >= 0 ? 0 : 999;

  return {
    packages: pkgResults,
    totalS2UnitsCeiling,
    totalNetRevenueCeiling,
    totalContributionCeiling,
    portfolioPnLCeiling,
    totalS2UnitsSplit,
    totalNetRevenueSplit,
    totalContributionSplit,
    portfolioPnLSplit,
    fixedCosts,
    annualPnLCeiling: portfolioPnLCeiling * 12,
    annualPnLSplit: portfolioPnLSplit * 12,
    portfolioBreakevenUnits,
    contributionMarginPctSplit,
    weightedAvgSellingPrice,
    s1ROI,
    monthsToBreakeven,
  };
}
